"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BoardManager = void 0;
const Board_1 = require("./Board");
class BoardManager {
    constructor() {
        this.boards = new Map();
        this.eventLog = [];
        this.outgoingEvents = [];
    }
    createBoard(boardId, width, height) {
        const board = new Board_1.Board(boardId, width, height);
        this.boards.set(boardId, board);
        return board;
    }
    getBoard(boardId) {
        return this.boards.get(boardId);
    }
    getBoardIds() {
        return [...this.boards.keys()];
    }
    // Record an event and apply it to the target board
    recordEvent(event) {
        this.eventLog.push(event);
        const board = this.boards.get(event.boardId);
        if (board) {
            board.applyEvent(event);
        }
    }
    // Advance all boards by one tick
    tickAll() {
        for (const board of this.boards.values()) {
            board.tick();
        }
        // Route cross-board events
        this.routeCrossBoardEvents();
    }
    // Advance a specific board by one tick
    tickBoard(boardId) {
        const board = this.boards.get(boardId);
        if (board) {
            board.tick();
        }
        this.routeCrossBoardEvents();
    }
    // Collect and route cross-board events
    routeCrossBoardEvents() {
        var _a;
        // In a real distributed system, this would send events over the network
        // For now, we route locally
        for (const cbe of this.outgoingEvents) {
            const destBoard = this.boards.get(cbe.toBoardId);
            if (destBoard) {
                // Create a CROSS_BOARD_EVENT and apply to destination board
                const event = {
                    id: `cbe-${cbe.fromBoardId}-${cbe.toBoardId}-${cbe.departureTick}`,
                    tick: cbe.departureTick,
                    boardId: cbe.toBoardId,
                    type: 'CROSS_BOARD_EVENT',
                    payload: cbe,
                    playerId: ((_a = cbe.payload) === null || _a === void 0 ? void 0 : _a.playerId) || '',
                };
                destBoard.applyEvent(event);
            }
        }
        this.outgoingEvents = [];
    }
    // Queue a cross-board event for routing
    queueCrossBoardEvent(cbe) {
        this.outgoingEvents.push(cbe);
    }
    // Get all events
    getEvents() {
        return [...this.eventLog];
    }
    // Get events for a specific board
    getEventsForBoard(boardId) {
        return this.eventLog.filter(e => e.boardId === boardId);
    }
    // Get events up to a specific tick
    getEventsUpToTick(tick) {
        return this.eventLog.filter(e => e.tick <= tick);
    }
    // Rollback to a specific tick — replay events from scratch
    rollbackToTick(targetTick) {
        // Filter events to keep only those up to targetTick
        const eventsToKeep = this.eventLog.filter(e => e.tick <= targetTick);
        // Clear all boards
        this.boards.clear();
        // Re-create boards and replay events
        // First, collect unique board IDs and their initial dimensions
        const boardConfigs = new Map();
        for (const event of eventsToKeep) {
            if (!boardConfigs.has(event.boardId)) {
                // Default board size — would be set by a BOARD_CREATED event in production
                boardConfigs.set(event.boardId, { width: 10, height: 10 });
            }
        }
        // Create boards
        for (const [boardId, config] of boardConfigs) {
            this.createBoard(boardId, config.width, config.height);
        }
        // Replay events in order
        for (const event of eventsToKeep) {
            const board = this.boards.get(event.boardId);
            if (board) {
                board.applyEvent(event);
            }
        }
        // Now advance ticks up to targetTick
        // We need to replay the tick simulation
        // The events have already been applied, but we need to simulate ticks
        // to rebuild the layer states (electricity, production, transport)
        // This is the core of event sourcing: state = f(events, ticks)
        // Truncate event log
        this.eventLog = eventsToKeep;
        // Simulate ticks from 0 to targetTick
        // First, reset all boards to tick 0 (just events applied, no tick simulation yet)
        for (const board of this.boards.values()) {
            board.state.tick = 0;
        }
        // Now advance to targetTick
        for (let t = 0; t < targetTick; t++) {
            this.tickAll();
        }
    }
    // Get all board states (snapshots)
    getAllSnapshots() {
        const snapshots = new Map();
        for (const [boardId, board] of this.boards) {
            snapshots.set(boardId, board.getSnapshot());
        }
        return snapshots;
    }
}
exports.BoardManager = BoardManager;



//////////////////
// WEBPACK FOOTER
// ../engine/dist/BoardManager.js
// module id = t+6h
// module chunks = 0